import axios from "axios";
import { STORAGE_KEYS, COOKIE_KEYS } from "../constants";
import { isTokenExpired } from "../utils/tokenUtils";
import { authService } from "../services/authService";
import { store } from "../store/store";
import { tokenRefreshed, logout } from "../store/slices/authSlice";
import { clearAuthData } from "../utils/authSync";
import { getCookie, setCookie, deleteCookie } from "../utils/cookieUtils";
import { TokenDto } from "../types/api";

if (!process.env.REACT_APP_API_BASE_URL) {
  // Fail loudly during development; in production the build pipeline must provide this.
  console.error(
    "[api] REACT_APP_API_BASE_URL is not set. All API requests will fail.",
  );
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 300,
  withCredentials: true,
});

export interface TokenWithTimestamp extends TokenDto {
  issued_at?: number;
}

// authService.refreshToken issues PUT /token through this same instance, so the
// interceptors below must never start a refresh while handling that request.
const TOKEN_ENDPOINT = "/token";

let refreshInFlight: Promise<TokenWithTimestamp> | null = null;

const doRefresh = async (): Promise<TokenWithTimestamp> => {
  const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
  const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const tokenData: TokenWithTimestamp | null = tokenRaw
    ? JSON.parse(tokenRaw)
    : null;

  // Backend returns both new access_token and new refresh_token on every refresh.
  // The new refresh_token must be saved — the old one is invalidated immediately.
  const newToken = await authService.refreshToken({
    access_token: tokenData?.access_token ?? "",
    refresh_token: refreshToken,
  });

  const newTokenWithTimestamp = {
    ...newToken,
    issued_at: Date.now() / 1000,
  };

  const { refresh_token: newRt, ...newAccessData } = newTokenWithTimestamp;
  setCookie(COOKIE_KEYS.ACCESS_TOKEN, JSON.stringify(newAccessData), 1);
  setCookie(COOKIE_KEYS.REFRESH_TOKEN, newRt, 7);

  store.dispatch(tokenRefreshed({ token: newTokenWithTimestamp }));

  return newTokenWithTimestamp;
};

// Single-flight, shared with useTokenManager: the backend rotates refresh tokens
// and drops the whole token family when one is replayed, so a proactive and a
// reactive refresh must never run concurrently.
export const refreshTokensOnce = (): Promise<TokenWithTimestamp> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().then(
      (token) => {
        refreshInFlight = null;
        return token;
      },
      (error: unknown) => {
        refreshInFlight = null;
        throw error;
      },
    );
  }
  return refreshInFlight;
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
    if (tokenRaw) {
      try {
        const tokenData = JSON.parse(tokenRaw);
        const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);
        if (refreshToken && !tokenData.refresh_token) {
          tokenData.refresh_token = refreshToken;
        }

        if (isTokenExpired(tokenData) && config.url !== TOKEN_ENDPOINT) {
          // Locally expired is not a dead session: the refresh token cookie may
          // still be valid, so rotate before sending rather than dropping the
          // Authorization header, which guarantees a 401.
          let accessToken = tokenData.access_token;
          try {
            const refreshed = await refreshTokensOnce();
            accessToken = refreshed.access_token || accessToken;
          } catch {
            // Nothing usable to refresh with — send what we have and let the
            // server decide; the response interceptor handles a genuine 401.
          }
          config.headers.Authorization = `Bearer ${accessToken}`;
          return config;
        }

        config.headers.Authorization = `Bearer ${tokenData.access_token}`;
      } catch (error) {
        console.error("Invalid token format:", error);
        deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
      const refreshToken = getCookie(COOKIE_KEYS.REFRESH_TOKEN);

      // A 401 on the token endpoint itself means the refresh token is dead;
      // refreshing here would recurse straight back into this interceptor.
      if (refreshToken && originalRequest.url !== TOKEN_ENDPOINT) {
        try {
          const newToken = await refreshTokensOnce();

          originalRequest.headers.Authorization = `Bearer ${newToken.access_token}`;
          return api(originalRequest);
        } catch (refreshError: unknown) {
          console.error(
            "Token refresh failed in axios interceptor:",
            axios.isAxiosError(refreshError)
              ? refreshError.response?.status
              : "network error",
          );

          // Only a real answer from the server ends the session: a timeout, a
          // dropped connection or a cold start must leave it intact.
          const serverAnswered =
            axios.isAxiosError(refreshError) && !!refreshError.response;
          const isRegistrationFlow = localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
          if (!isRegistrationFlow && serverAnswered) {
            clearAuthData();
            store.dispatch(logout());
          }
          return Promise.reject(refreshError);
        }
      }

      const isRegistrationFlow = localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
      const wasAuthenticated = !!(tokenRaw || refreshToken);

      if (!isRegistrationFlow && wasAuthenticated) {
        clearAuthData();
        store.dispatch(logout());
      }
    }

    return Promise.reject(error);
  },
);

export default api;
